// src/pages/Phmj.jsx
import React, { useState, useEffect } from 'react';
import apiClient from '../../api/client'; // Import apiClient
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorDisplay from '../../components/common/ErrorDisplay';
import PageTransitionWrapper from '../../components/layout/PageTransitionWrapper';
import { FaUsers } from 'react-icons/fa';

const Phmj = () => {
    const [phmjList, setPhmjList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        let isCancelled = false;
        let progressInterval;
        const MIN_LOADING_TIME = 1000;
        const DELAY_AFTER_LOAD = 700;

        const fetchPhmjData = async () => {
            setIsLoading(true);
            setError(null);
            setProgress(0);

            const startTime = Date.now();
            progressInterval = setInterval(() => {
                const elapsedTime = Date.now() - startTime;
                let calculatedProgress = (elapsedTime / MIN_LOADING_TIME) * 100;
                if (calculatedProgress > 99) calculatedProgress = 99;
                if (!isCancelled) {
                    setProgress(Math.floor(calculatedProgress));
                }
            }, 50);

            try {
                const data = await apiClient.getPhmjList();
                // Pastikan selalu berbentuk array, apapun bentuk respons API-nya
                const list = Array.isArray(data) ? data : (data?.items ?? []);
                setPhmjList(list);
            } catch (err) {
                if (!isCancelled) {
                    const errorMessage = err.message || 'Gagal mengambil data PHMJ. Silakan coba lagi nanti.';
                    setError(errorMessage);
                    console.error('API Fetch Error:', err);
                }
            } finally {
                clearInterval(progressInterval);
                if (!isCancelled) {
                    setProgress(100);
                    setTimeout(() => {
                        setIsLoading(false);
                    }, DELAY_AFTER_LOAD);
                }
            }
        };
        fetchPhmjData();
        return () => {
            isCancelled = true;
            clearInterval(progressInterval);
        };
    }, []);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <LoadingSpinner progress={progress} />
            </div>
        );
    }

    if (error) {
        return <ErrorDisplay message={error} />;
    }

    return (
        <div className="bg-gray-50 min-h-screen py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">

                {/* 🌟 Header Halaman — konsisten dengan Pelkat & KontakKorsek */}
                <PageTransitionWrapper>
                    <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-10 relative overflow-hidden border-b-8 border-blue-700">
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-50/40 to-white transform skew-x-3 -translate-x-1/2 opacity-70"></div>
                        <div className="relative z-10 text-center">
                            <div className="flex justify-center mb-3">
                                <div className="p-4 rounded-full bg-blue-50 text-blue-700 text-3xl">
                                    <FaUsers />
                                </div>
                            </div>
                            <h1 className="text-4xl sm:text-5xl font-black text-gray-900 mb-2">
                                ANGGOTA PHMJ
                            </h1>
                            <p className="text-lg text-gray-600 max-w-3xl mx-auto font-medium">
                                Pengurus Harian Majelis Jemaat GPIB Kasih Karunia Badung Bali.
                            </p>
                        </div>
                    </div>

                    {/* 📋 Area Konten Utama */}
                    <div className="mt-8 bg-white rounded-2xl shadow-xl p-6 sm:p-10 relative">
                        {phmjList.length > 0 ? (
                            <div className="overflow-x-auto rounded-xl border border-gray-200">
                                <table className="w-full text-left table-auto border-collapse min-w-[500px]">
                                    <thead>
                                        <tr className="bg-blue-700 text-white text-base font-bold">
                                            <th className="px-6 py-4 w-1/2">NAMA</th>
                                            <th className="px-6 py-4 w-1/4">NO. TELP</th>
                                            <th className="px-6 py-4 w-1/4">JABATAN</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {phmjList.map((anggota, index) => (
                                            <tr
                                                key={anggota.id ?? index}
                                                className={`border-b border-gray-200 hover:bg-blue-50 transition-colors duration-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                                                    }`}
                                            >
                                                <td className="px-6 py-4">{anggota.name}</td>
                                                <td className="px-6 py-4">{anggota.phone}</td>
                                                <td className="px-6 py-4">{anggota.role}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-16 text-gray-400">
                                <p className="text-lg font-medium mb-1">Data Belum Tersedia</p>
                                <p className="text-sm">Data anggota PHMJ sedang dalam proses pembaharuan.</p>
                            </div>
                        )}
                    </div>
                </PageTransitionWrapper>
            </div>
        </div>
    );
};

export default Phmj;